import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Language configurations - 16 languages (13 Indian + 2 Foreign + English)
export const LANGUAGE_CONFIG = {
  'en-IN': { code: 'en-IN', display: 'English', nativeName: 'English', font: 'Noto Sans' },
  'hi-IN': { code: 'hi-IN', display: 'Hindi', nativeName: '\u0939\u093F\u0928\u094D\u0926\u0940', font: 'Noto Sans Devanagari' },
  'bn-IN': { code: 'bn-IN', display: 'Bengali', nativeName: '\u09AC\u09BE\u0982\u09B2\u09BE', font: 'Noto Sans Bengali' },
  'ta-IN': { code: 'ta-IN', display: 'Tamil', nativeName: '\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD', font: 'Noto Sans Tamil' },
  'te-IN': { code: 'te-IN', display: 'Telugu', nativeName: '\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41', font: 'Noto Sans Telugu' },
  'mr-IN': { code: 'mr-IN', display: 'Marathi', nativeName: '\u092E\u0930\u093E\u0920\u0940', font: 'Noto Sans Devanagari' },
  'gu-IN': { code: 'gu-IN', display: 'Gujarati', nativeName: '\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0', font: 'Noto Sans Gujarati' },
  'kn-IN': { code: 'kn-IN', display: 'Kannada', nativeName: '\u0C95\u0CA8\u0CCD\u0CA8\u0CA1', font: 'Noto Sans Kannada' },
  'ml-IN': { code: 'ml-IN', display: 'Malayalam', nativeName: '\u0D2E\u0D32\u0D2F\u0D3E\u0D33\u0D02', font: 'Noto Sans Malayalam' },
  'or-IN': { code: 'or-IN', display: 'Odia', nativeName: '\u0B13\u0B21\u0B3C\u0B3F\u0B06', font: 'Noto Sans Oriya' },
  'pa-IN': { code: 'pa-IN', display: 'Punjabi', nativeName: '\u0A2A\u0A70\u0A1C\u0A3E\u0A2C\u0A40', font: 'Noto Sans Gurmukhi' },
  'as-IN': { code: 'as-IN', display: 'Assamese', nativeName: '\u0985\u09B8\u09AE\u09C0\u09AF\u09BC\u09BE', font: 'Noto Sans Bengali' },
  'ur-IN': { code: 'ur-IN', display: 'Urdu', nativeName: '\u0627\u0631\u062F\u0648', font: 'Noto Nastaliq Urdu' },
  'sa-IN': { code: 'sa-IN', display: 'Sanskrit', nativeName: '\u0938\u0902\u0938\u094D\u0915\u0943\u0924\u092E\u094D', font: 'Noto Sans Devanagari' },
  'fr-FR': { code: 'fr-FR', display: 'French', nativeName: 'Fran\u00E7ais', font: 'Noto Sans' },
  'es-ES': { code: 'es-ES', display: 'Spanish', nativeName: 'Espa\u00F1ol', font: 'Noto Sans' }
};

// Business terms that must NOT be translated (remain English)
export const DO_NOT_TRANSLATE_TERMS = [
  'Sales', 'Revenue', 'Stock', 'SKU', 'Margin', 'Discount', 'Returns',
  'Footfall', 'Target', 'Sell-through', 'Inventory', 'Vendor', 'GM',
  'ASP', 'AOV', 'MRP', 'GRN', 'OTB', 'OTIF', 'SLA', 'KPI', 'WOS',
  'SOH', 'COGS', 'GMROI', 'UPT', 'ATV', 'LFL', 'EBITDA', 'PO',
  'StockSense', 'SalesIQ', 'Customer360', 'ProductMix', 'VendorLink',
  'StorePulse', 'PromoIQ', 'ReturnIQ', 'DemandFlow', 'ExecutiveBrain',
  'ReasonBot', 'RetailIQ'
];

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [locale, setLocale] = useState('en-IN');
  const [translationCache, setTranslationCache] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  // Load saved locale on mount (same key as ReasonBot)
  useEffect(() => {
    const savedLocale = localStorage.getItem('preferredLocale');
    if (savedLocale && LANGUAGE_CONFIG[savedLocale]) {
      setLocale(savedLocale);
      applyFont(savedLocale);
    }
  }, []);

  // Apply font for language
  const applyFont = useCallback((targetLocale) => {
    const config = LANGUAGE_CONFIG[targetLocale];
    if (config) {
      document.documentElement.style.setProperty('--app-font-family', config.font);
      // Load Google Fonts dynamically
      if (!document.getElementById(`font-${targetLocale}`)) {
        const link = document.createElement('link');
        link.id = `font-${targetLocale}`;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${config.font.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    }
  }, []);

  // Change language (same behavior as ReasonBot)
  const changeLanguage = useCallback((newLocale) => {
    if (LANGUAGE_CONFIG[newLocale]) {
      setLocale(newLocale);
      localStorage.setItem('preferredLocale', newLocale);
      applyFont(newLocale);
      // Clear cache for new language (will re-translate on demand)
    }
  }, [applyFont]);

  // Translate text with caching and business-friendly tone
  const translate = useCallback(async (text, cacheKey = null) => {
    // If English, return original
    if (locale === 'en-IN' || !text) {
      return text;
    }

    // Check cache
    const key = cacheKey || `${locale}:${text.substring(0, 100)}`;
    if (translationCache[key]) {
      return translationCache[key];
    }

    // Call translation API
    try {
      setIsTranslating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/chatbot/translate`,
        { text, target_locale: locale },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const translated = response.data?.translated_text || text;
      
      // Cache the translation
      setTranslationCache(prev => ({ ...prev, [key]: translated }));
      
      return translated;
    } catch (err) {
      console.error('Translation failed:', err);
      return text; // Fallback to English
    } finally {
      setIsTranslating(false);
    }
  }, [locale, translationCache]);

  // Batch translate multiple texts
  const translateBatch = useCallback(async (texts) => {
    if (locale === 'en-IN' || !texts || texts.length === 0) {
      return texts;
    }

    const results = await Promise.all(
      texts.map((text, i) => translate(text, `batch:${locale}:${i}:${text.substring(0, 50)}`))
    );
    return results;
  }, [locale, translate]);

  // Check if a term should not be translated
  const shouldPreserve = useCallback((term) => {
    return DO_NOT_TRANSLATE_TERMS.some(t => 
      term.toLowerCase().includes(t.toLowerCase())
    );
  }, []);

  const value = useMemo(() => ({
    locale,
    setLocale: changeLanguage,
    languageConfig: LANGUAGE_CONFIG[locale],
    allLanguages: LANGUAGE_CONFIG,
    translate,
    translateBatch,
    isTranslating,
    isEnglish: locale === 'en-IN',
    shouldPreserve,
    doNotTranslateTerms: DO_NOT_TRANSLATE_TERMS
  }), [locale, changeLanguage, translate, translateBatch, isTranslating, shouldPreserve]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Simple hook for quick translations
export const useTranslation = () => {
  const { translate, translateBatch, locale, isEnglish, isTranslating } = useLanguage();
  
  return {
    t: translate,
    tBatch: translateBatch,
    locale,
    isEnglish,
    isTranslating
  };
};

export default LanguageContext;
